import React, { useState } from 'react';
import { FiSave, FiX, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import { Flow, CommandStep, FlowType } from '../../../../../types/flow';
import { isMongoObjectId } from '../../../../../types/tvCommandTypeMapper';
import { Button } from '../ui/FlowButton';
import { Input } from '../ui/FlowInput';
import { Textarea } from '../ui/FlowTextArea';
import { TVRemote } from './TVRemote';
import { CommandTimeline } from './CommandTimeline';
import { RawCommandPreview } from './RawCommandPreview';
import { AdSlotFilters, MultiSelect, filterOptions } from './AdSlotFilters';
import { cn } from '../lib/utils';
import { toast } from 'react-toastify';

const MAX_COMMANDS_PER_FLOW = 40;

interface FlowEditorProps {
  flow: Flow | null;
  flowType: FlowType;
  hasUnsavedChanges: boolean;
  onSave: (flow: Flow) => void;
  onDiscard: () => void;
  onFlowChange: (flow: Flow) => void;
  onDelete: (id: string) => void;
}

export const FlowEditor: React.FC<FlowEditorProps> = ({
  flow,
  flowType,
  hasUnsavedChanges,
  onSave,
  onDiscard,
  onFlowChange,
  onDelete,
}) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const handleNameChange = (name: string) => {
    if (flow) onFlowChange({ ...flow, name });
  };

  const handleDescriptionChange = (description: string) => {
    if (flow) onFlowChange({ ...flow, description });
  };

  const handleCommandPress = (command: string, label: string) => {
    if (!flow) return;
    if (!canAddAnotherCommand()) return;
    const newCommand: CommandStep = {
      id: generateId(),
      type: 'command',
      command,
      label,
      ...(flowType === 'base' ? { sleep: 1, timeout: undefined } : {}),
    };
    onFlowChange({ ...flow, commands: [...flow.commands, newCommand] });
  };

  const handleAddSleep = (duration: number) => {
    if (!flow) return;
    if (!canAddAnotherCommand()) return;
    const newSleep: CommandStep = {
      id: generateId(),
      type: 'sleep',
      duration,
    };
    onFlowChange({ ...flow, commands: [...flow.commands, newSleep] });
  };

  const handleAddCustomCommand = (command: string, label?: string) => {
    if (!flow) return;
    if (!canAddAnotherCommand()) return;
    const newCommand: CommandStep = {
      id: generateId(),
      type: 'command',
      command,
      label: label || 'Custom',
      ...(flowType === 'base' ? { sleep: 1, timeout: undefined } : {}),
    };
    onFlowChange({ ...flow, commands: [...flow.commands, newCommand] });
  };

  const handleReorder = (commands: CommandStep[]) => {
    if (flow) onFlowChange({ ...flow, commands });
  };

  const handleDelete = (id: string) => {
    if (!flow) return;
    onFlowChange({
      ...flow,
      commands: flow.commands.filter(c => c.id !== id),
    });
  };

  const handleDeleteClick = () => {
    if (!flow) return;

    toast(
      ({ closeToast }) => (
        <div className="adslot-toast-confirm">
          <div className="adslot-toast-confirm-title">
            Are you sure you want to delete this Flow?
          </div>

          <div className="adslot-toast-confirm-actions">
            <button
              type="button"
              className="adslot-toast-btn adslot-toast-btn-secondary"
              onClick={() => closeToast?.()}
            >
              Cancel
            </button>

            <button
              type="button"
              className="adslot-toast-btn adslot-toast-btn-danger"
              onClick={() => {
                closeToast?.();
                onDelete(flow.id);
              }}
            >
              Delete
            </button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false },
    );
  };

  const handleDuplicate = (id: string) => {
    if (!flow) return;
    if (!canAddAnotherCommand()) return;
    const index = flow.commands.findIndex(c => c.id === id);
    if (index === -1) return;
    const original = flow.commands[index];
    const duplicate = { ...original, id: generateId() };
    const newCommands = [...flow.commands];
    newCommands.splice(index + 1, 0, duplicate);
    onFlowChange({ ...flow, commands: newCommands });
  };

  const handleUpdateSleep = (id: string, duration: number) => {
    if (!flow) return;
    onFlowChange({
      ...flow,
      commands: flow.commands.map(c => (c.id === id ? { ...c, duration } : c)),
    });
  };

  const handleUpdateCommand = (id: string, patch: Partial<CommandStep>) => {
    if (!flow) return;
    onFlowChange({
      ...flow,
      commands: flow.commands.map(c => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  const handleDefaultChange = (isDefault: boolean) => {
    if (flow) onFlowChange({ ...flow, isDefault });
  };

  const handlePreOsChange = (osVersions: string[]) => {
    if (!flow) return;
    const filters = flow.filters || {
      adSlots: [],
      osVersions: [],
      makes: [],
      adTypes: [],
      destTypes: [],
      countries: [],
    };
    onFlowChange({ ...flow, filters: { ...filters, osVersions } });
  };

  const warnTooManyCommands = () => {
    toast.warn(
      'Flow exceeds max command size. Consider splitting into multiple flows',
      {
        toastId: 'flow-max-commands-warning', // prevents spam
      },
    );
  };

  const canAddAnotherCommand = () => {
    if (!flow) return false;
    const maxCommands = flowType === 'base' ? 1 : MAX_COMMANDS_PER_FLOW;
    if (flow.commands.length >= maxCommands) {
      if (flowType === 'base') {
        toast.warn('Base command scripts can contain only one command', {
          toastId: 'base-command-max-warning',
        });
        return false;
      }
      warnTooManyCommands();
      return false;
    }
    return true;
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!flow?.name.trim()) {
      errors.push('Flow name is required');
    }

    if (!flow?.commands.length) {
      errors.push('Flow cannot be empty. Add at least one command.');
    }

    if (flowType === 'base' && flow && flow.commands.length > 1) {
      errors.push('Base command scripts can contain only one command.');
    }

    const longSleep = flow?.commands.find(
      c => c.type === 'sleep' && c.duration && c.duration > 60,
    );
    if (longSleep) {
      errors.push(
        `Warning: Sleep of ${longSleep.duration}s may slow down tests.`,
      );
    }

    if (flow && flowType !== 'base' && flow.commands.length > MAX_COMMANDS_PER_FLOW) {
      errors.push(
        `Warning: This flow has ${flow.commands.length} commands. Consider splitting into multiple flows (max is ${MAX_COMMANDS_PER_FLOW}).`,
      );
    }

    setValidationErrors(errors);
    return errors.filter(e => !e.startsWith('Warning')).length === 0;
  };

  const handleSaveClick = () => {
    if (!flow) return;
    if (validate()) {
      onSave(flow);
      // Success toast handled in parent
    } else {
      toast.error('Please fix validation errors before saving');
    }
  };

  if (!flow) {
    return (
      <div className="adslot-editor-empty">
        <div className="adslot-editor-empty-inner">
          <div className="adslot-editor-empty-icon">📺</div>
          <h3 className="adslot-editor-empty-title">Select a flow to edit</h3>
          <p className="adslot-editor-empty-subtitle">
            Choose a flow from the sidebar or create a new one
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="adslot-editor-root">
      {/* Header */}
      <div className="adslot-editor-header">
        <Input
          value={flow.name}
          onChange={e => handleNameChange(e.target.value)}
          placeholder="Untitled flow"
          className="adslot-editor-name-input"
        />
        <div className="adslot-editor-header-actions">
          <Button
            variant="outline"
            onClick={onDiscard}
            disabled={!hasUnsavedChanges}
          >
            <FiX className="adslot-icon-xs adslot-icon-inline" />
            Discard
          </Button>
          {isMongoObjectId(flow.id) && (
            <Button
              variant="outline"
              className="adslot-btn-danger"
              onClick={handleDeleteClick}
            >
              <FiTrash2 className="adslot-icon-xs adslot-icon-inline" />
              Delete
            </Button>
          )}
          <Button onClick={handleSaveClick}>
            <FiSave className="adslot-icon-xs adslot-icon-inline" />
            Save
          </Button>
        </div>
      </div>

      {['post', 'country', 'vpn'].includes(flowType) && (
        <div className="adslot-editor-options">
          <label className="adslot-editor-checkbox">
            <input
              type="checkbox"
              checked={!!flow.isDefault}
              onChange={e => handleDefaultChange(e.target.checked)}
            />
            <span>Use as default {flowType} script</span>
          </label>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="adslot-editor-validation">
          {validationErrors.map((error, i) => (
            <div
              key={i}
              className={cn(
                'adslot-banner',
                error.startsWith('Warning')
                  ? 'adslot-banner-warning'
                  : 'adslot-banner-error',
              )}
            >
              <FiAlertTriangle className="adslot-icon-xs" />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}

      {/* AdSlot Filters */}
      {flowType === 'adslot' && (
        <AdSlotFilters
          filters={flow.filters}
          onFiltersChange={filters => onFlowChange({ ...flow, filters })}
        />
      )}

      {flowType === 'pre' && (
        <div className="adslot-filters">
          <h3 className="adslot-filters-title">Pre Step Filters</h3>
          <div className="adslot-filters-grid">
            <MultiSelect
              label="OS Version(s)"
              options={filterOptions.osVersions}
              selected={flow.filters?.osVersions || []}
              onChange={handlePreOsChange}
            />
          </div>
        </div>
      )}

      {/* Description */}
      <div className="adslot-editor-description">
        <Textarea
          value={flow.description || ''}
          onChange={e => handleDescriptionChange(e.target.value)}
          placeholder="Add a description for this flow..."
        />
      </div>

      {/* Active Filters */}
      {(flowType === 'adslot' || flowType === 'pre') && flow.filters && (
        <div className="adslot-editor-active-filters">
          {flowType === 'adslot' && flow.filters.adSlots.map(slot => (
            <span key={slot} className="chip chip-primary chip-sm">
              AdSlot: {slot}
            </span>
          ))}
          {flow.filters.osVersions.map(os => (
            <span key={os} className="chip chip-info chip-sm">
              OS: {os}
            </span>
          ))}
          {flowType === 'adslot' && flow.filters.makes.map(make => (
            <span key={make} className="chip chip-muted chip-sm">
              {make}
            </span>
          ))}
          {flowType === 'adslot' && flow.filters.adTypes.map(type => (
            <span key={type} className="chip chip-warning chip-sm">
              {type}
            </span>
          ))}
          {flowType === 'adslot' && flow.filters.destTypes.map(dest => (
            <span key={dest} className="chip chip-muted chip-sm">
              {dest}
            </span>
          ))}
          {flowType === 'adslot' && flow.filters.countries.map(country => (
            <span key={country} className="chip chip-success chip-sm">
              {country}
            </span>
          ))}
        </div>
      )}

      {/* Main content */}
      <div className="adslot-editor-main-content">
        {flowType !== 'base' && (
          <div className="adslot-editor-remote">
            <TVRemote onCommandPress={handleCommandPress} />
          </div>
        )}

        <div className="adslot-editor-timeline">
          <CommandTimeline
            commands={flow.commands}
            onReorder={handleReorder}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onUpdateSleep={handleUpdateSleep}
            onAddSleep={handleAddSleep}
            onAddCustomCommand={handleAddCustomCommand}
            onUpdateCommand={handleUpdateCommand}
            isBaseFlow={flowType === 'base'}
          />
          <RawCommandPreview commands={flow.commands} />
        </div>
      </div>
    </div>
  );
};
